/**
 * DetailSidebar - Right sidebar with horizontal tabs
 * Matches canvas styling with tabs for different panes
 */

import { useState, useEffect, useMemo } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { JourneyPane } from './panes/JourneyPane'
import { HealthScorePane } from './panes/HealthScorePane'
import { AICoachPane } from './panes/AICoachPane'
import { UploadDocsPane } from './panes/UploadDocsPane'
import { FlowCoachPane } from './panes/FlowCoachPane'
import { FileUp, Bot, Activity, Map, Sparkles } from 'lucide-react'

interface TabConfig {
  id: string
  label: string
  icon: typeof FileUp
  component: React.ComponentType<{ onOpenGoalWizard?: () => void }> | null
  disabledInFullAI?: boolean
  disabledInFlow?: boolean
  onlyInFlow?: boolean
}

const BASE_TABS: TabConfig[] = [
  {
    id: 'journey',
    label: 'Journey',
    icon: Map,
    component: null, // Rendered specially to pass props
  },
  {
    id: 'health',
    label: 'Health',
    icon: Activity,
    component: HealthScorePane,
  },
  {
    id: 'coach',
    label: 'AI Coach',
    icon: Bot,
    component: AICoachPane,
    disabledInFullAI: true,
    disabledInFlow: true,
  },
  {
    id: 'docs',
    label: 'Documents',
    icon: FileUp,
    component: UploadDocsPane,
  },
]

// Flow Coach tab - prepended when in Flow mode
const FLOW_COACH_TAB: TabConfig = {
  id: 'flow-coach',
  label: 'Flow Coach',
  icon: Sparkles,
  component: FlowCoachPane,
  onlyInFlow: true,
}

interface DetailSidebarProps {
  onOpenGoalWizard?: () => void
}

export function DetailSidebar({ onOpenGoalWizard }: DetailSidebarProps) {
  const { activeMode } = useWorkspaceStore()
  // Default to AI Coach on Dashboard mode, Flow Coach on Flow mode, Journey otherwise
  const getDefaultTab = () => {
    if (activeMode === 'dashboard') return 'coach'
    if (activeMode === 'flow') return 'flow-coach'
    return 'journey'
  }
  const [activeTab, setActiveTab] = useState(getDefaultTab())

  // Switch to appropriate tab when entering different modes
  useEffect(() => {
    if (activeMode === 'dashboard') {
      setActiveTab('coach')
    } else if (activeMode === 'flow') {
      setActiveTab('flow-coach')
    }
  }, [activeMode])

  // Build tabs list based on mode
  const availableTabs = useMemo(() => {
    let tabs = [...BASE_TABS]

    // Add Flow Coach tab at the beginning when in Flow mode
    if (activeMode === 'flow') {
      tabs = [FLOW_COACH_TAB, ...tabs]
    }

    // Filter out disabled tabs
    return tabs.filter((tab) => {
      if (tab.disabledInFullAI && activeMode === 'full-ai') return false
      if (tab.disabledInFlow && activeMode === 'flow') return false
      return true
    })
  }, [activeMode])

  // If current tab becomes disabled, switch to first available
  const currentTab = availableTabs.find((t) => t.id === activeTab)
    ? activeTab
    : availableTabs[0]?.id || 'journey'

  return (
    <div className="w-[320px] border-l border-warm-200 flex-shrink-0 bg-white flex flex-col overflow-hidden">
      <Tabs
        value={currentTab}
        onValueChange={setActiveTab}
        className="h-full flex flex-col overflow-hidden"
      >
        {/* Tab Headers */}
        <div className="border-b border-warm-200 px-3 pt-3 flex-shrink-0">
          <TabsList className="w-full bg-warm-100 h-8">
            {availableTabs.map((tab) => {
              const Icon = tab.icon
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex-1 px-2 h-7"
                  title={tab.label}
                >
                  <Icon size={14} className="mr-1" />
                  <span className="text-[11px] truncate">{tab.label}</span>
                </TabsTrigger>
              )
            })}
          </TabsList>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {availableTabs.map((tab) => {
            // JourneyPane is rendered specially to pass the onOpenGoalWizard callback
            if (tab.id === 'journey') {
              return (
                <TabsContent
                  key={tab.id}
                  value={tab.id}
                  className="m-0 p-4 h-full overflow-y-auto"
                >
                  <JourneyPane onOpenGoalWizard={onOpenGoalWizard} />
                </TabsContent>
              )
            }

            // FlowCoachPane handles its own padding and scrolling
            if (tab.id === 'flow-coach') {
              const Component = tab.component
              if (!Component) return null
              return (
                <TabsContent
                  key={tab.id}
                  value={tab.id}
                  className="m-0 h-full"
                >
                  <Component />
                </TabsContent>
              )
            }

            const Component = tab.component
            if (!Component) return null

            return (
              <TabsContent
                key={tab.id}
                value={tab.id}
                className="m-0 p-4 h-full overflow-y-auto"
              >
                <Component />
              </TabsContent>
            )
          })}
        </div>
      </Tabs>
    </div>
  )
}
